import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { formatDate } from "@angular/common";
import { MessageService } from "primeng/api";
import { forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { CatExpertRouterLink } from "../../app-routing-enum";
import {
  FilterProperties,
  keysToSelectedFolder,
  newFolderDialogContent,
} from "../../main-container/studio/studio-constants";
import { HttpService } from "../../services/http.service";
import { ProjectOverviewService } from "../../services/project-overview.service";
import { StateService } from "../../services/state.service";
import { MultiSelectionService } from "../../services/multi-selection.service";
import { PublishedGroupingService } from "../../services/published-grouping.service";

@Component({
  selector: "app-common-browse-experiments",
  templateUrl: "./common-browse-experiments.component.html",
  styleUrl: "./common-browse-experiments.component.scss",
})
export class CommonBrowseExperimentsComponent implements OnChanges {
  @Input() dialogContent: any = {};
  @Input() showDialog: boolean = false;
  @Input() radioSelection: boolean = false;
  @Input() preSelected: any = [];
  @Input() isSinglePlanSelect: boolean = false;
  @Input() showAccordion: boolean = true;
  @Input() ignoredExperiments: any[] = [];
  // When true, on Proceed we still fetch plans for the selected experiments
  // (so `plansMapforExperiment` is populated), but skip the default follow-up
  // calls (e.g. bulk user/owner lookup) and emit immediately after plans arrive.
  @Input() skipProceedDefaultCalls: boolean = false;
  @Input() hideFolderCheckbox: boolean = false;
  // Opt-in Studio-style on-demand fetching.
  // When true, the component fetches its own folder/experiment data via
  // `getMyFoldersExperiments` / `getSharedFoldersExperiments` on dialog open,
  // tab switch, folder drill, and breadcrumb back — no `[data]` input needed.
  @Input() fetchOnDemand: boolean = false;
  // When set (only applied in fetchOnDemand mode), client-side filters the
  // `experiments` array by this key_account_id. Folders are never filtered.
  @Input() keyAccountFilterId: string = "";
  // Studio-style `experiment_types` filter used in the fetch payload.
  @Input() experimentTypesFilter: string[] = ["planogram", "space_assortment"];

  @Input() isClientSidePagination: boolean = true;
  @Input() isUseSelectionService: boolean = false;

  // When the parent removes an experiment from its own selected list, pass the
  // remaining selected experiment IDs here so the component can deselect the
  // removed ones automatically (both UI and internal state).
  @Input() activeSelectedExperimentIds: string[] | null = null;

  @Output() closeDialog = new EventEmitter<any>();

  parentId: string = "root";
  selectedExperiments: any[] = [];
  selectedOnlyExperiments: any[] = []; // Assign the experiments when check the checkbox by experiments wise

  experimentList: { [key: string]: any[] } = {
    experiments: [],
    shared: [],
    published: [],
  };

  currentTab: "shared" | "experiments" | "published" =
    CatExpertRouterLink.Experiments as "experiments";

  breadcrumbTrailMap: { [key: string]: any[] } = {
    shared: [],
    experiments: [],
    published: [],
  };

  keysToSelectedFolder = keysToSelectedFolder;
  newFolderDialogContent = newFolderDialogContent;
  wholeFolderData: any = {};
  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  totalRecords: number = 0;
  selectedList: any = JSON.parse(sessionStorage.getItem("storeIds")!) || [];
  folderSelectionMap: { [id: string]: boolean } = {};
  experimentSelectionMap: { [id: string]: boolean } = {};
  // Folders checked by the user; survives root re-fetch (unlike folderSelectionMap).
  selectedFolderIds: Set<string> = new Set();
  // Folders unchecked while an ancestor folder is selected.
  excludedFolderIds: Set<string> = new Set();
  // Ancestor folder IDs (breadcrumb) at the time an item's selection was set explicitly.
  private selectionPathMap = new Map<string, string[]>();
  // Seed selection maps once per dialog open so later root fetches keep the user's edits.
  private selectionSeedPending: boolean = true;
  showTableFilters: boolean = false;
  keyAccountList: any = [];
  plansList: any[] = [];
  selectedPlans: any[] = [];
  planogramUsers: { [id: string]: any } = {};
  enableExpSelection: boolean = true;
  originalTableCols: any[] = [];
  plansMapforExperiment: { [experimentId: string]: any[] } = {};
  selectedPlansforExperiment: { [experimentId: string]: any[] } = {};
  selectedPlanId: string = "";
  selectedSinglePlan: any = null;

  searchText!: string;
  filterExperimentTypes: any = [];
  filterKeyAccountIds!: [];
  filterCountries!: [];
  filterRegions!: [];
  filterStatus!: [];
  sorting: any[] = [];
  dateRange: Date[] = [];

  selectedGroupByOption: any[] = [];
  groupedPublishedData: any = {};
  expandedGroupedNodeIds: Set<string> = new Set();
  publishedSelectionIds: Set<string> = new Set();
  directPublishedSelectionIds: Set<string> = new Set();
  parentPublishedSelectionIds: Set<string> = new Set();

  parentSelectedRows: any = [];

  constructor(
    private httpService: HttpService,
    public stateService: StateService,
    private messageService: MessageService,
    private projectService: ProjectOverviewService,
    public multiSelectService: MultiSelectionService,
    private publishedGroupingService: PublishedGroupingService,
  ) {
    this.getKeyAccounts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes &&
      changes["showDialog"] &&
      changes["showDialog"].currentValue === true
    ) {
      this.resetDialogSelectionState();
      this.selectionSeedPending = true;

      if (this.fetchOnDemand) {
        // Dialog just opened — fetch root of the current tab (Studio-style).
        if (this.isCurrentTabPublished) {
          this.loadPublishedTab();
        } else {
          this.fetchAndLoad("root", this.currentTab === "shared");
        }
      }
    } else if (
      changes["showDialog"]?.currentValue === false &&
      !changes["showDialog"].firstChange
    ) {
      // Dialog closed — clear transient state but keep persisted selections
      // so the next open can preselect already chosen experiments.
      this.clearSelectionState(true);
    }

    // When the parent removes an experiment from its active selection, deselect
    // it in this component so UI and internal state stay in sync.
    if (changes && changes["activeSelectedExperimentIds"]) {
      const newIds: string[] | null =
        changes["activeSelectedExperimentIds"].currentValue;

      this.activeSelectedExperimentIds = newIds;
    }

    // When the parent removes an experiment from its active selection, deselect
    // it in this component so UI and internal state stay in sync.
    if (changes && changes["activeSelectedExperimentIds"]) {
      const newIds: string[] | null =
        changes["activeSelectedExperimentIds"].currentValue;

      this.activeSelectedExperimentIds = newIds;
    }

    this.originalTableCols = JSON.parse(
      JSON.stringify(this.dialogContent.tableColumns),
    );
  }

  private clearSelectionState(keepPersistedSelection: boolean = false): void {
    this.experimentSelectionMap = {};
    this.folderSelectionMap = {};
    this.selectedFolderIds.clear();
    this.excludedFolderIds.clear();
    this.selectionPathMap.clear();
    this.selectedExperiments = [];
    this.selectedOnlyExperiments = [];
    if (!keepPersistedSelection) {
      this.selectedList = [];
    }
    this.plansMapforExperiment = {};
    this.selectedPlansforExperiment = {};
    this.multiSelectService.clear();
    if (!keepPersistedSelection) {
      sessionStorage.removeItem("storeIds");
    }
  }

  private resetDialogSelectionState(): void {
    this.currentTab = "experiments";
    this.selectedExperiments = [];
    this.selectedOnlyExperiments = [];
    this.selectedPlansforExperiment = {};
    this.plansMapforExperiment = {};
    this.selectedPlanId = "";
    this.selectedSinglePlan = null;
    this.searchText = "";
    this.sorting = [];
    this.dateRange = [];
    this.groupedPublishedData = {};
    this.expandedGroupedNodeIds.clear();
    this.publishedSelectionIds.clear();
    this.directPublishedSelectionIds.clear();
    this.parentPublishedSelectionIds.clear();
    this.parentSelectedRows = [];
    this.selectedFolderIds.clear();
    this.excludedFolderIds.clear();
    this.selectionPathMap.clear();
  }

  initializeSelectionMaps() {
    this.folderSelectionMap = {}; // no pre-selection for folders
    this.experimentSelectionMap = {};

    // Populate experimentSelectionMap with true for IDs in selectedList
    this.selectedList.forEach((id: string) => {
      this.experimentSelectionMap[id] = true;
    });

    (this.activeSelectedExperimentIds || []).forEach((id: string) => {
      this.experimentSelectionMap[id] = true;
    });
  }

  applyPreSelectedExperimentsAndPlans() {
    if (
      !this.preSelected ||
      typeof this.preSelected !== "object" ||
      this.isSinglePlanSelect
    )
      return;

    const expIds = Object.keys(this.preSelected);
    if (!expIds.length) return;

    expIds.forEach((expId) => {
      // Mark experiment as selected
      this.experimentSelectionMap[expId] = true;

      // Try to find the experiment in your current tree and add to selected
      const experiment = this.findExperimentById(
        this.experimentList[this.currentTab],
        expId,
      );

      if (experiment) {
        experiment.isChecked = true;
        if (!this.selectedExperiments.find((e) => e.id === experiment.id)) {
          this.selectedExperiments.push(experiment);
          this.selectedOnlyExperiments.push(experiment);
        }

        // Also mark parent folders as selected (for UI checkbox)
        this.markParentFoldersAsSelected(expId);
      }
    });

    // Fetch plans for all preselected experiments in one batched call (the
    // plans-list endpoint already accepts multiple experiment_ids at once),
    // instead of firing a separate request per experiment.
    this.getPlansForExperiment(expIds.map((id) => ({ id })));
  }

  findExperimentById(items: any[], id: string): any {
    for (const item of items) {
      if (item.id === id && item.experiment_type !== "folder") {
        return item;
      }

      if (item.children?.length) {
        const found = this.findExperimentById(item.children, id);
        if (found) return found;
      }

      if (item.experiments?.length) {
        const found = this.findExperimentById(item.experiments, id);
        if (found) return found;
      }

      if (item.folders?.length) {
        const found = this.findExperimentById(item.folders, id);
        if (found) return found;
      }
    }
    return null;
  }

  markParentFoldersAsSelected(expId: string) {
    const walk = (items: any[]): boolean => {
      let found = false;

      for (const item of items) {
        if (item.experiment_type === "folder" && item.children?.length) {
          item.isChecked = this.folderSelectionMap[item.id] || false;
          const childFound = walk(item.children);
          if (childFound) {
            this.folderSelectionMap[item.id] = true;
            found = true;
          }
        } else if (item.id === expId) {
          return true;
        }
      }

      return found;
    };

    walk(this.experimentList[this.currentTab]);
  }

  processExperimentData(sectionData: any): any[] {
    if (!this.enableExpSelection) {
      this.experimentList[this.currentTab] = this.selectedExperiments;
    }

    if (!sectionData) return [];

    const inherited = this.getInheritedSelection();

    const processItems = (items: any[]): any[] => {
      return (items || [])
        .filter((item: any) => !this.ignoredExperiments.includes(item.id))
        .map((item: any) => {
          const isFolder =
            item?.type === "folder" ||
            item?.experiment_type === "folder" ||
            item?.experiments;

          const children = item.children ? processItems(item.children) : [];

          // Determine isChecked:
          // - For experiments: check experimentSelectionMap
          // - For folders: selected if all children are selected
          let isChecked = false;
          let isInherited = false;
          if (isFolder) {
            const childItems = [
              ...(item.folders || []),
              ...(item.experiments || []),
              ...children,
            ];

            const allChildrenSelected =
              childItems.length > 0 &&
              childItems.every((child) =>
                child.experiment_type === "folder"
                  ? this.folderSelectionMap[child.id]
                  : this.experimentSelectionMap[child.id],
              );

            const inheritedFolder =
              inherited && !this.excludedFolderIds.has(item.id);
            isInherited =
              inheritedFolder && !this.selectedFolderIds.has(item.id);
            // On-demand folders have no loaded children, so the map would only hold stale values.
            isChecked =
              allChildrenSelected ||
              (!this.fetchOnDemand && !!this.folderSelectionMap[item.id]) ||
              this.selectedFolderIds.has(item.id) ||
              inheritedFolder;
            this.folderSelectionMap[item.id] = isChecked;
          } else {
            const explicit = this.experimentSelectionMap[item.id];
            isInherited = explicit === undefined && inherited;
            isChecked = explicit ?? inherited;
          }

          return {
            ...item,
            experiment_type: isFolder ? "folder" : item?.experiment_type,
            isEditable: false,
            isLoading: false,
            isRowDisabled: item.id === sessionStorage.getItem("expId"),
            isChecked,
            __inheritedSelection: isInherited,
            hasLoadedChildren: !!item.children,
            children,
          };
        });
    };

    const folders = processItems(sectionData.folders || []);
    const experiments = processItems(sectionData.experiments || []);

    this.prepareTableFilterOptions();
    return [...folders, ...experiments];
  }

  // Function for get the key accounts
  getKeyAccounts() {
    this.projectService.getUserKeyAccounts().subscribe((val) => {
      if (val) {
        this.keyAccountList = val;
        this.prepareTableFilterOptions();
      }
    });
  }

  // Function to render filter dropdown options
  prepareTableFilterOptions() {
    this.dialogContent.tableColumns.map((each: any) => {
      if (each.filterType === "dropdown") {
        if (each.value === "key_account") {
          each.filterOptions = this.keyAccountList.map((item: any) => ({
            label: item.name,
            value: item.id,
          }));
        } else if (each.value === "country") {
          const seen = new Set();
          each.filterOptions = this.keyAccountList
            .filter((item: any) => {
              const key = item.country;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((item: any) => ({
              label: item.country,
              value: item.country,
            }));
        } else if (each.value === "region") {
          const seen = new Set();
          each.filterOptions = this.keyAccountList
            .filter((item: any) => {
              const key = item.region;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((item: any) => ({
              label: item.region,
              value: item.region,
            }));
        }
      }
    });
  }

  dialogTabChange(tabKey: "shared" | "experiments" | "published") {
    this.currentTab = tabKey;
    this.resetFilterValues();
    if (this.fetchOnDemand) {
      if (tabKey === "published") {
        this.loadPublishedTab();
      } else {
        this.fetchAndLoad("root", tabKey === "shared");
      }
      this.multiSelectService.clear();
      return;
    }

    if (tabKey === "published") {
      this.loadPublishedTab();
      return;
    }

    this.resetToRoot();
  }

  navigateExperiment(event: any) {
    const experiment = event.data;

    if (experiment.experiment_type === "folder") {
      if (this.fetchOnDemand) {
        // Sub-folder drill fetches /folders/{id}/contents from the server
        // (matches Studio's behaviour for non-root under both tabs).
        this.fetchAndLoad(experiment.id, false, {
          id: experiment.id,
          label: experiment.name,
          section: this.currentTab,
        });
        return;
      }

      // Update breadcrumb trail
      this.breadcrumbTrailMap[this.currentTab].push({
        id: experiment.id,
        label: experiment.name,
        section: this.currentTab,
      });

      this.parentId = experiment.id;
      this.experimentList[this.currentTab] =
        this.processExperimentData(experiment);
      this.showTableFilters = false;
    }
  }

  onNavigateBack(currentItem: any) {
    if (currentItem.id === "root") {
      if (this.fetchOnDemand) {
        if (this.isCurrentTabPublished) {
          this.loadPublishedTab();
        } else {
          this.fetchAndLoad("root", this.currentTab === "shared");
        }
        return;
      }
      this.resetToRoot();
      return;
    }

    if (this.fetchOnDemand) {
      // Trim breadcrumbs up to and including the clicked crumb, then re-fetch.
      const clickedIndex = this.breadcrumbTrailMap[this.currentTab].findIndex(
        (item) => item.id === currentItem.id,
      );
      if (clickedIndex !== -1) {
        this.breadcrumbTrailMap[this.currentTab].splice(clickedIndex + 1);
      }
      this.fetchAndLoad(currentItem.id, false);
      return;
    }

    const parent = this.findFolderById(
      this.processExperimentData(this.wholeFolderData[this.currentTab]),
      currentItem.id,
    );

    // Remove all breadcrumb items AFTER parent.id (keep parent.id itself)
    const parentIndex = this.breadcrumbTrailMap[this.currentTab].findIndex(
      (item) => item.id === parent.id,
    );
    if (parentIndex !== -1) {
      this.breadcrumbTrailMap[this.currentTab].splice(parentIndex + 1);
    }

    if (parent) {
      this.parentId = parent.id;
      this.experimentList[this.currentTab] = this.processExperimentData(parent);
    } else {
      // Fallback to root
      this.resetToRoot();
    }
  }

  resetToRoot() {
    this.parentId = "root";
    // Initialize breadcrumb trail with root label
    this.breadcrumbTrailMap[this.currentTab] = [
      {
        id: "root",
        label: this.getBreadcrumbLabel(),
        section: this.currentTab,
      },
    ];

    this.experimentList[this.currentTab] = this.processExperimentData(
      this.wholeFolderData[this.currentTab],
    );
  }

  getBreadcrumbLabel(): string {
    const labels: Record<string, string> = {
      experiments: "My Experiments",
      shared: "Shared",
      published: "Published",
    };

    return labels[this.currentTab] ?? "Shared";
  }

  // Payload for `/folders/**/contents`. Server-side filters are applied for
  // `key_account_ids` and `experiment_types` when set — no client-side
  // filtering needed. Keys with empty/absent values are omitted to avoid
  // Pydantic 422 errors (the backend rejects empty strings / empty arrays for
  // these optional fields).
  private buildStudioPayload(): any {
    const payload: any = {
      page: this.currentPage,
      page_size: this.pageSize,
    };
    if (this.searchText) {
      payload["search_text"] = this.searchText;
    }
    const keyAccountIds = this.keyAccountFilterId
      ? [this.keyAccountFilterId]
      : this.filterKeyAccountIds;
    if (keyAccountIds?.length) {
      payload.key_account_ids = keyAccountIds;
    }
    if (this.experimentTypesFilter && this.experimentTypesFilter.length) {
      payload.experiment_types = this.experimentTypesFilter;
    }
    if (this.sorting.length) {
      payload["sort"] = this.sorting;
    }
    this.dateRange?.length && (payload["created_at"] = this.dateRange);

    return payload;
  }

  // Fetch folder contents (root or a specific folder) and refresh the visible
  // list. `parentId === 'root'` refreshes the current tab; otherwise it drills
  // into a sub-folder (and optionally pushes a breadcrumb crumb).
  private fetchAndLoad(
    parentId: string,
    isSharedRoot: boolean,
    breadcrumbToPush?: { id: string; label: string; section: string },
  ): void {
    const apiUrl = isSharedRoot
      ? this.stateService.apiList.experiments.getSharedFoldersExperiments()
      : this.stateService.apiList.experiments.getMyFoldersExperiments(parentId);
    const payload = this.buildStudioPayload();

    this.stateService.addCalledApi(apiUrl);
    this.httpService.postMethod(apiUrl, payload).subscribe({
      next: (response: any) => {
        this.stateService.removeCalledApi(apiUrl);
        const filtered = response || { folders: [], experiments: [] };
        // Tag folders — matches Studio's convention so downstream code
        // (checkbox logic, drill-down detection) recognises them.
        (filtered.folders || []).forEach(
          (f: any) => (f.experiment_type = "folder"),
        );

        if (parentId === "root") {
          // Root of current tab — mirror `wholeFolderData` so `resetToRoot()`
          // works if the user later navigates back to root.
          this.wholeFolderData = {
            ...this.wholeFolderData,
            [this.currentTab]: filtered,
          };
          if (this.selectionSeedPending) {
            this.initializeSelectionMaps();
            this.selectionSeedPending = false;
          }
          this.resetToRoot();
        } else {
          if (breadcrumbToPush) {
            this.breadcrumbTrailMap[this.currentTab].push(breadcrumbToPush);
          }
          this.parentId = parentId;
          this.experimentList[this.currentTab] =
            this.processExperimentData(filtered);
          this.showTableFilters = false;
        }

        this.currentPage = response.page;
        this.pageSize = response.page_size;
        this.totalPages = response.total_pages;
        this.totalRecords =
          response?.total ??
          response.total_experiments + response.total_folders;

        // Re-apply preselection so previously-checked items stay checked
        // when they reappear on this level.
        setTimeout(() => this.applyPreSelectedExperimentsAndPlans(), 0);
      },
      error: () => {
        this.stateService.removeCalledApi(apiUrl);
        // Leave dialog open; user can retry via tab switch or cancel.
      },
    });
  }

  findFolderById(items: any[], id: string): any {
    for (const item of items) {
      if (item.id === id && item.experiment_type === "folder") {
        return item;
      }
      if (item.folders?.length) {
        const found = this.findFolderById(item.folders, id);
        if (found) return found;
      }
    }
    return null;
  }

  multipleActions(event: any) {
    // Rows checked only via an ancestor folder are already covered by that folder.
    const selectedItems = (
      Array.isArray(event.data) ? event.data : [event.data]
    ).filter((item: any) => !item?.__inheritedSelection);
    const parentSelectedItems = Array.isArray(event?.parentSelectedItems)
      ? event.parentSelectedItems
      : [];
    if (this.radioSelection && selectedItems?.length) {
      this.selectedPlanId = selectedItems[0].id;
    }

    // Sync the selected items based on their type (parent or experiments only for published tab)
    if (this.isCurrentTabPublished) {
      this.syncSelectedItemsByType(selectedItems, parentSelectedItems);
      return;
    }

    const selectedCheckedItems = selectedItems.filter(
      (item: any) => item.isChecked,
    );
    const newExperiments = selectedCheckedItems.filter((item: any) => {
      return ["planogram", "space_assortment"].includes(item.experiment_type);
    });

    // Step 2: Combine old and new, avoiding duplicates based on unique property (e.g., id)
    newExperiments.forEach((expData: any) => {
      if (!this.selectedExperiments.some((item) => item.id === expData.id)) {
        this.selectedExperiments.push(expData);

        // Assign the experiments when check the checkbox by experiments wise
        this.selectedOnlyExperiments.push(expData);
      }
    });

    const unSelectedExperiments = selectedItems.filter(
      (item: any) => !item.isChecked,
    );

    if (selectedCheckedItems.length) {
      selectedCheckedItems.forEach((item: any) => {
        if (
          (item.experiment_type === "folder" ||
            item.experiment_type === undefined) &&
          !["planogram", "space_assortment"].includes(item.experiment_type)
        ) {
          // Set folder selection state
          this.folderSelectionMap[item.id] = item.isChecked;
          this.updateSelectedFolderIds(item.id, item.isChecked);
          // Propagate folder selection to children (folders & experiments)
          this.toggleChildren(item, item.isChecked);
        } else {
          this.setExperimentSelectionState(item, item.isChecked, true);
        }
      });
    }

    if (unSelectedExperiments.length) {
      this.updateUncheckedSelections(unSelectedExperiments);
    }

    // Header "deselect all" emits an empty list, so clear the visible rows explicitly.
    if ("isAllSelected" in event && event.checked === false) {
      this.updateUncheckedSelections(
        (this.experimentList[this.currentTab] || []).filter(
          (item: any) => !item.isChecked,
        ),
      );
    }
  }

  // Method to update the un checked folder's and experiments
  updateUncheckedSelections(unSelectedExperiments: any) {
    unSelectedExperiments.forEach((item: any) => {
      item.__inheritedSelection = false;
      if (
        (item.experiment_type === "folder" ||
          item.experiment_type === undefined) &&
        !["planogram", "space_assortment"].includes(item.experiment_type)
      ) {
        this.folderSelectionMap[item.id] = item.isChecked;
        this.updateSelectedFolderIds(item.id, item.isChecked);
        this.toggleChildren(item, item.isChecked);
      } else {
        this.setExperimentSelectionState(item, item.isChecked, true);
      }
    });
  }

  private updateSelectedFolderIds(folderId: string, checked: boolean): void {
    if (!folderId) return;
    const inherited = this.getInheritedSelection();
    const wasChecked =
      this.selectedFolderIds.has(folderId) ||
      (inherited && !this.excludedFolderIds.has(folderId));
    if (wasChecked === checked) return;

    this.selectedFolderIds.delete(folderId);
    this.excludedFolderIds.delete(folderId);
    if (checked && !inherited) this.selectedFolderIds.add(folderId);
    if (!checked && inherited) this.excludedFolderIds.add(folderId);

    this.clearDescendantSelections(folderId, checked);
    this.recordSelectionPath(folderId);
  }

  private getAncestorFolderIds(): string[] {
    return (this.breadcrumbTrailMap[this.currentTab] || [])
      .map((crumb: any) => crumb.id)
      .filter((id: string) => id && id !== "root");
  }

  // Nearest explicitly selected/excluded ancestor in the breadcrumb decides the default.
  private getInheritedSelection(): boolean {
    const ancestors = this.getAncestorFolderIds();
    for (let i = ancestors.length - 1; i >= 0; i--) {
      if (this.selectedFolderIds.has(ancestors[i])) return true;
      if (this.excludedFolderIds.has(ancestors[i])) return false;
    }
    return false;
  }

  private recordSelectionPath(id: string): void {
    this.selectionPathMap.set(id, this.getAncestorFolderIds());
  }

  // Toggling a folder resets explicit choices made inside it.
  private clearDescendantSelections(folderId: string, checked: boolean): void {
    this.selectionPathMap.forEach((path, id) => {
      if (!path.includes(folderId)) return;

      this.selectedFolderIds.delete(id);
      this.excludedFolderIds.delete(id);
      delete this.folderSelectionMap[id];

      const explicit = this.experimentSelectionMap[id];
      if (explicit === true && checked) {
        // Keep: it is still part of selectedOnlyExperiments.
        return;
      }
      if (explicit === true) {
        this.selectedExperiments = this.selectedExperiments.filter(
          (item) => item.id !== id,
        );
        this.selectedOnlyExperiments = this.selectedOnlyExperiments.filter(
          (item) => item.id !== id,
        );
        delete this.selectedPlansforExperiment[id];
      }
      delete this.experimentSelectionMap[id];
      this.selectionPathMap.delete(id);
    });
  }

  toggleChildren(folder: any, checked: boolean) {
    const children = [...(folder.folders || []), ...(folder.experiments || [])];

    children.forEach((child) => {
      if (
        (child.experiment_type === "folder" ||
          child.experiment_type === undefined) &&
        !["planogram", "space_assortment"].includes(child.experiment_type)
      ) {
        child.isChecked = checked;
        this.folderSelectionMap[child.id] = checked;
        this.toggleChildren(child, checked); // recursive call
      } else {
        this.setExperimentSelectionState(child, checked, true);
      }
    });
  }

  private setExperimentSelectionState(
    experiment: any,
    checked: boolean,
    syncPlans: boolean = false,
  ): void {
    if (!experiment?.id) {
      return;
    }

    experiment.isChecked = checked;
    experiment.__hasPartialChildSelection = false;
    this.experimentSelectionMap[experiment.id] = checked;
    this.recordSelectionPath(experiment.id);

    if (syncPlans) {
      this.syncPlanSelectionForExperiment(experiment, checked);
    }

    if (checked) {
      if (!this.selectedExperiments.some((item) => item.id === experiment.id)) {
        this.selectedExperiments.push(experiment);
      }

      if (
        !this.selectedOnlyExperiments.some((item) => item.id === experiment.id)
      ) {
        this.selectedOnlyExperiments.push(experiment);
      }
      return;
    }

    this.selectedExperiments = this.selectedExperiments.filter(
      (item) => item.id !== experiment.id,
    );
    this.selectedOnlyExperiments = this.selectedOnlyExperiments.filter(
      (item) => item.id !== experiment.id,
    );
    delete this.selectedPlansforExperiment[experiment.id];
  }

  private syncPlanSelectionForExperiment(
    experiment: any,
    checked: boolean,
  ): void {
    const plans = this.plansMapforExperiment[experiment.id];

    if (!Array.isArray(plans) || !plans.length) {
      return;
    }

    plans.forEach((plan: any) => {
      plan.isChecked = checked;
    });

    if (checked) {
      this.selectedPlansforExperiment[experiment.id] = [...plans];
      return;
    }

    delete this.selectedPlansforExperiment[experiment.id];
  }

  private syncExperimentSelectionFromPlans(expData: any): void {
    if (!expData?.id) {
      return;
    }

    const plans = this.plansMapforExperiment[expData.id] || [];
    const selectedPlans = this.selectedPlansforExperiment[expData.id] || [];
    const totalPlans = plans.length;
    const selectedCount = selectedPlans.length;

    expData.isChecked = totalPlans > 0 && selectedCount === totalPlans;
    expData.__hasPartialChildSelection =
      selectedCount > 0 && selectedCount < totalPlans;
    this.experimentSelectionMap[expData.id] = expData.isChecked;

    if (selectedCount > 0) {
      if (!this.selectedExperiments.some((item) => item.id === expData.id)) {
        this.selectedExperiments.push(expData);
      }

      if (expData.__hasPartialChildSelection) {
        this.selectedOnlyExperiments = this.selectedOnlyExperiments.filter(
          (item) => item.id !== expData.id,
        );
      }
      return;
    }

    this.selectedExperiments = this.selectedExperiments.filter(
      (item) => item.id !== expData.id,
    );
    this.selectedOnlyExperiments = this.selectedOnlyExperiments.filter(
      (item) => item.id !== expData.id,
    );
  }

  addExperiments(btnFlag: boolean) {
    this.onClose(btnFlag);
  }

  onClose(btnFlag: boolean = false) {
    if (!btnFlag) {
      this.selectedExperiments = [];
      this.selectedPlansforExperiment = {};
      this.selectedSinglePlan = null;
      this.resetDialogSelectionState();
      this.callProceedToEmit(btnFlag);
      return;
    }

    // If the current tab is Published and there are selected parent rows,
    // fetch the published experiment list alone and then proceed further actions.
    if (this.isCurrentTabPublished && this.parentSelectedRows.length > 0) {
      this.getPublishExperimentListAloneApiCall();
      return;
    }

    // If using the multi-selection service and there are selected experiments, fetch plans for those experiments
    if (this.isUseSelectionService && this.selectedExperimentsList.length) {
      this.getPlansForExperiment(this.selectedExperimentsList, btnFlag);
      return;
    }

    if (
      this.selectedOnlyExperiments.length > 0 ||
      this.selectedFolderIds.size
    ) {
      // This will call only when checkbox checked by experiments/folders wise
      this.getPlansForExperiment(this.selectedOnlyExperiments, btnFlag);
    } else {
      // This will call only when checkbox checked by plans wise
      this.callProceedToEmit(btnFlag);
    }
  }

  // Helper method to emit the selected experiment and selected plans to parent component
  callProceedToEmit(btnFlag: boolean = false) {
    const emitData: any = {
      action: btnFlag,
      selectedExperiments: this.selectedExperiments,
      plansMapforExperiment: this.isSinglePlanSelect
        ? this.selectedSinglePlan
        : this.selectedPlansforExperiment,
    };

    // Emit selectedPlanId only if radioSelection is enabled and a plan is selected
    if (this.radioSelection && this.selectedPlanId) {
      emitData["selectedData"] = this.selectedPlanId;
    }

    // If using the multi-selection service and there are selected experiments, override the selectedExperiments in emitData
    if (this.isUseSelectionService && this.selectedExperimentsList.length) {
      emitData.selectedExperiments = this.selectedExperimentsList;
    }
    this.closeDialog.emit(emitData);
    this.multiSelectService.clear();
    if (!this.isSinglePlanSelect) {
      this.showDialog = false;
    }
  }

  // Helper method to call the actions method
  getTableAction(event: { action: { callBack: keyof any }; data: any }) {
    const callback = event.action.callBack;
    if (
      typeof callback === "string" &&
      typeof (this as any)[callback] === "function"
    ) {
      (this as any)[callback](event);
    }
  }

  experimentRowCheckboxChange(event: any) {
    const row = event?.data;
    const checked = !!event?.checked;

    if (!row) {
      return;
    }
    row.__inheritedSelection = false;

    if (
      (row.experiment_type === "folder" || row.experiment_type === undefined) &&
      !["planogram", "space_assortment"].includes(row.experiment_type)
    ) {
      this.folderSelectionMap[row.id] = checked;
      this.updateSelectedFolderIds(row.id, checked);
      this.toggleChildren(row, checked);
      return;
    }

    this.setExperimentSelectionState(row, checked, true);

    if (this.isCurrentTabPublished) {
      this.syncSinglePublishedSelectionState(row, checked, "direct");
    }
  }

  paginationAction(event: any) {
    this.currentPage = event.data.page;
    this.pageSize = event.data.page_size;
    if (this.isCurrentTabPublished) {
      this.loadPublishedTab();
      return;
    }
    this.fetchAndLoad(this.parentId, this.currentTab === "shared");
  }

  // Helper method to reset the multiple action data
  sortingAction(event: any) {
    this.sorting = event.data.sort;
    this.currentPage = event.data.page;
    this.pageSize = event.data.page_size;
    if (this.isCurrentTabPublished) {
      this.loadPublishedTab();
      return;
    }
    this.fetchAndLoad(this.parentId, this.currentTab === "shared");
  }

  filterSearchAction(rowData: any) {
    this.currentPage = 1;
    this.pageSize = 20;
    this.searchText = rowData.data || null;
    if (this.isCurrentTabPublished) {
      this.loadPublishedTab();
      return;
    }
    this.fetchAndLoad(this.parentId, this.currentTab === "shared");
  }

  filterCallback(event: any) {
    this.showTableFilters = !this.showTableFilters;
  }

  filterByDate(event: any) {
    this.dateRange =
      Array.isArray(event?.data) && event.data.length > 0 ? event.data : [];
    if (this.isCurrentTabPublished) {
      this.loadPublishedTab();
      return;
    }
    this.fetchAndLoad(this.parentId, this.currentTab === "shared");
  }

  getPlansForExperiment(
    selectedItems: any,
    btnFlag: boolean = false,
    onComplete?: () => void,
  ) {
    // Always normalize selectedItems into an array of experiments
    const experiments = Array.isArray(selectedItems)
      ? selectedItems
      : [selectedItems.data];

    const experimentsToLoad = btnFlag
      ? experiments
      : experiments.filter(
          (exp: any) =>
            !Object.prototype.hasOwnProperty.call(
              this.plansMapforExperiment,
              exp.id,
            ),
        );

    if (!btnFlag && experimentsToLoad.length === 0) {
      return;
    }

    const experimentIds = experimentsToLoad.map((exp) => exp.id);

    const apiUrl = this.stateService.apiList.planogram.getPlanogramList;
    const payload: any = {
      experiment_ids: experimentIds,
      include_additional_details: experimentIds.length > 1 ? false : true,
    };

    // Folder selection only applies to Proceed, not accordion/preselection fetches.
    const folderIds = btnFlag ? Array.from(this.selectedFolderIds) : [];
    if (folderIds.length) {
      payload.folder_ids = folderIds;
    }
    if (this.keyAccountFilterId) {
      payload.key_account_ids = [this.keyAccountFilterId];
    }

    const suppressLoader = this.skipProceedDefaultCalls && btnFlag;

    this.commonApiCall(
      apiUrl,
      "postMethod",
      payload,
      (response) => {
        if (response) {
          experimentIds.forEach((id) => {
            const plans = response[id]?.plans || [];
            this.plansMapforExperiment[id] = plans;

            const preselectedPlanIds = this.preSelected?.[id] || [];

            this.selectedPlansforExperiment[id] = plans.filter((plan: any) => {
              const isPreselected = preselectedPlanIds.includes(plan.id);
              if (isPreselected) {
                plan["isChecked"] = true;
              }
              return isPreselected;
            });

            const experiment = experiments.find((exp: any) => exp.id === id);
            if (experiment) {
              if (experiment.isChecked && !preselectedPlanIds.length) {
                this.syncPlanSelectionForExperiment(experiment, true);
              }
              this.syncExperimentSelectionFromPlans(experiment);
            }
          });

          if (this.isSinglePlanSelect && this.preSelected?.exp_id) {
            const plans = response[this.preSelected?.exp_id]?.plans || [];
            this.plansMapforExperiment[this.preSelected?.exp_id] = plans;
            let selectedPlan = plans.find((plan: any) => {
              return plan.id === this.preSelected?.plan_id;
            });
            if (selectedPlan?.id) {
              this.toggleRadioSelection(
                "",
                response[this.preSelected?.exp_id],
                selectedPlan,
              );
            }
          }

          // Experiments returned via selected folders (not individually checked).
          const folderExperimentIds = folderIds.length
            ? Object.keys(response).filter(
                (id) =>
                  !experimentIds.includes(id) &&
                  response[id] &&
                  typeof response[id] === "object",
              )
            : [];
          const finalize = (folderExperiments: any[]) => {
            folderExperiments.forEach((exp: any) => {
              this.plansMapforExperiment[exp.id] =
                response[exp.id]?.plans || [];
              this.setExperimentSelectionState(exp, true, true);
            });
            const resultExperimentIds = [
              ...experimentIds,
              ...folderExperiments.map((exp: any) => exp.id),
            ];

            // Kick off the next batch (if any) right away so plans-list requests
            // stay sequential; the bulk-user-detail lookup below runs alongside it.
            onComplete?.();

            if (this.skipProceedDefaultCalls && btnFlag) {
              this.emitAfterPlans(btnFlag, resultExperimentIds);
              return;
            }

            this.getBulkUserList(btnFlag, resultExperimentIds);
          };

          if (folderExperimentIds.length) {
            this.resolveFolderExclusions(folderExperimentIds, (ids) =>
              ids.length
                ? this.getFolderExperimentDetails(ids, finalize)
                : finalize([]),
            );
          } else {
            finalize([]);
          }
        }
      },
      suppressLoader,
    );
  }

  // Plans-list only returns `{ plans }` per experiment, so fetch row details separately.
  private getFolderExperimentDetails(
    experimentIds: string[],
    callback: (experiments: any[]) => void,
  ): void {
    const apiUrl = this.stateService.apiList.planogram.getExperimentDetails();
    const payload = {
      experiment_ids: experimentIds,
      fields: [
        "id",
        "name",
        "experiment_type",
        "country",
        "region",
        "key_account",
        "created_at",
      ],
    };

    this.commonApiCall(apiUrl, "postMethod", payload, (response: any) => {
      const experiments = (response?.experiments || []).filter(
        (exp: any) =>
          exp?.id &&
          experimentIds.includes(exp.id) &&
          !this.ignoredExperiments.includes(exp.id),
      );
      callback(experiments);
    });
  }

  // Emit immediately with plans populated, skipping the bulk user fetch
  private emitAfterPlans(btnFlag: boolean, experimentIds: string[]) {
    const filtered = experimentIds.reduce((obj: any, key) => {
      obj[key] = this.plansMapforExperiment[key] || [];
      return obj;
    }, {});
    this.selectedPlansforExperiment = {
      ...this.selectedPlansforExperiment,
      ...filtered,
    };
    this.callProceedToEmit(btnFlag);
  }

  getBulkUserList(btnFlag: boolean = false, experimentIds: any) {
    const userIds = new Set<string>();
    Object.values(this.plansMapforExperiment).forEach((exp: any) => {
      if (Array.isArray(exp)) {
        exp.forEach((plan: any) => {
          const userId = plan.created_by || plan.user;
          if (userId) {
            userIds.add(userId);
          }
        });
      }
    });
    const payload = {
      users_list: Array.from(userIds),
    };
    this.stateService.addCalledApi("get-users");
    this.stateService.getBulkUserDetailsService(payload).subscribe((val) => {
      if (val) {
        this.stateService.removeCalledApi("get-users");
        val.forEach((userDetails: any) => {
          this.planogramUsers[userDetails.id] = userDetails;
        });

        Object.values(this.plansMapforExperiment).forEach((exp: any) => {
          if (Array.isArray(exp)) {
            exp.forEach((plan: any) => {
              let createdBy = plan.created_by || plan.user;
              if (createdBy in this.planogramUsers) {
                plan["owner_by"] = this.planogramUsers[createdBy];
              }
              return plan;
            });
          }
        });

        // This will call, when exeriments only selected without plans selections
        if (btnFlag) {
          const filteredPlansMapforExperiment = Object.keys(
            this.plansMapforExperiment,
          )
            .filter((key) => experimentIds.includes(key))
            .reduce((obj: any, key) => {
              obj[key] = this.plansMapforExperiment[key];
              return obj;
            }, {});

          this.selectedPlansforExperiment = {
            ...this.selectedPlansforExperiment,
            ...filteredPlansMapforExperiment,
          };

          this.callProceedToEmit(btnFlag);
        }
      }
    });
  }

  formatHumanReadableDate(dateStr: string): string {
    const inputDateObj = new Date(dateStr + "Z");
    const now = new Date();

    const inputDay = new Date(
      inputDateObj.getFullYear(),
      inputDateObj.getMonth(),
      inputDateObj.getDate(),
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let label: string;

    if (inputDay.getTime() === today.getTime()) {
      label = "Today";
    } else if (inputDay.getTime() === yesterday.getTime()) {
      label = "Yesterday";
    } else {
      // Optional: format older dates like "Jul 7, 2025"
      return formatDate(inputDateObj, "MMM d, y, h:mm a", "en-US");
    }

    const time = formatDate(inputDateObj, "h:mm a", "en-US");
    return `${label}, ${time}`;
  }

  toggleRowSelection(event: any, expData: any, plan: any) {
    const isChecked = event.checked;

    plan["isChecked"] = isChecked;

    if (!this.selectedPlansforExperiment[expData.id]) {
      this.selectedPlansforExperiment[expData.id] = [];
    }

    if (isChecked) {
      if (!this.selectedPlansforExperiment[expData.id].includes(plan)) {
        this.selectedPlansforExperiment[expData.id].push(plan);
      }
    } else {
      this.selectedPlansforExperiment[expData.id] =
        this.selectedPlansforExperiment[expData.id].filter(
          (item) => item.id !== plan.id,
        );

      if (this.selectedPlansforExperiment[expData.id].length === 0) {
        delete this.selectedPlansforExperiment[expData.id];
      }
    }

    this.syncExperimentSelectionFromPlans(expData);
  }

  toggleRadioSelection(event: any, expData: any, plan: any) {
    this.selectedSinglePlan = plan;
    this.selectedExperiments = [expData];
  }

  enableExperimentSelection() {
    this.enableExpSelection = true;
    this.dialogContent.tableColumns = this.originalTableCols;
    this.initializeSelectionMaps();
    this.resetToRoot();
  }

  getString(val: any, column?: any) {
    if (column && column?.value === "status") {
      let textVal = this.stateService.titleRemoveUnderscore(val);
      if (typeof textVal === "string") {
        return textVal != "" ? textVal : "";
      }
      return;
    }
    if (column && column.value === "experiment_type") {
      return val === "space_assortment" ? "Space Planogram" : val;
    }
    if (typeof val === "string") {
      return val != "" ? val : "";
    }

    return val != null ? val : "";
  }

  commonApiCall(
    apiUrl: string,
    method: string,
    params: any,
    callback: (response: any, params: any) => void,
    suppressLoader: boolean = false,
  ) {
    if (!suppressLoader) this.stateService.addCalledApi(apiUrl);
    this.httpService[method as keyof HttpService](apiUrl, params).subscribe(
      (response: any) => {
        if (!suppressLoader) this.stateService.removeCalledApi(apiUrl);
        callback(response, params);
      },
      (err: any) => {
        this.messageService.clear();
        if (err.status === 400) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: "Something went wrong!",
            detail: err.error.message,
            sticky: true,
          });
        } else if (err.status === 422) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: err.error.detail?.[0]?.msg || "Something went wrong!",
            detail: err.error.detail,
            sticky: true,
          });
        } else if (err.status !== 401) {
          this.messageService.add({
            key: "c",
            severity: "warn",
            summary: err.error?.message || "Something went wrong!",
            detail: err?.error?.traceback,
            sticky: true,
          });
        }
        if (!suppressLoader) this.stateService.removeCalledApi(apiUrl);
      },
    );
  }

  // Helper method to call the filter action
  filterAction(rowData: any) {
    const filterMap: Record<string, keyof FilterProperties> = {
      experiment_type: "filterExperimentTypes",
      key_account: "filterKeyAccountIds",
      country: "filterCountries",
      region: "filterRegions",
      status: "filterStatus",
    };
    const targetKey = filterMap[rowData.action.key];
    if (targetKey) {
      this.updateFilterProperty(targetKey, rowData.data);
    }
    this.currentPage = 1;
    this.pageSize = 20;
    if (this.isCurrentTabPublished) {
      this.loadPublishedTab();
      return;
    }
    this.fetchAndLoad(this.parentId, this.currentTab === "shared");
  }

  private updateFilterProperty<K extends keyof FilterProperties>(
    key: K,
    value: FilterProperties[K],
  ) {
    (this as any)[key] = value;
  }

  resetFilterValues() {
    this.filterExperimentTypes = [];
    this.filterKeyAccountIds = [];
    this.filterCountries = [];
    this.filterRegions = [];
    this.filterStatus = [];
    this.dateRange = [];
    this.searchText = "";
    this.sorting = [];
    this.showTableFilters = false;

    (this.dialogContent.tableColumns || []).forEach((column: any) => {
      column.selectedFilterValues = [];
      column.appliedFilterValues = [];
      column.filterSearchText = "";
      column.filteredFilterOptions = null;
    });

    this.pageSize = 20;
    this.currentPage = 1;
    this.totalPages = 0;
    this.totalRecords = 0;
  }

  // Getter to get the selected rows from the multi select service
  get selectedExperimentsList() {
    return this.multiSelectService.selectedRowsList() || [];
  }

  get isSelectionInvalid(): boolean {
    if (this.isCurrentTabPublished) {
      return this.parentSelectedRows.length > 0
        ? !this.parentSelectedRows.length
        : !this.selectedExperiments.length &&
            this.publishedSelectionIds.size === 0;
    }

    return this.isSinglePlanSelect
      ? !this.selectedSinglePlan
      : this.radioSelection
        ? !this.selectedPlanId
        : this.isUseSelectionService
          ? !this.selectedExperimentsList.length
          : !this.selectedExperiments.length && !this.selectedFolderIds.size;
  }

  get isCurrentTabPublished() {
    return this.currentTab === "published";
  }

  private syncSelectedItemsByType(
    selectedItems: any[],
    parentSelectedItems: any[] = [],
  ) {
    const publishedSelectedItems: any[] = [];

    for (const item of selectedItems) {
      if (item.is_parent) {
        parentSelectedItems.push(item);
      } else {
        publishedSelectedItems.push(item);
      }
    }

    this.syncParentSelectionState(parentSelectedItems);
    this.syncPublishedSelectionState(publishedSelectedItems);
  }

  getPublishExperimentListAloneApiCall() {
    const apiUrl = this.stateService.apiList.experiments.publishedExperiments;
    const payload: any = {
      page: this.currentPage,
      experiment_types: this.filterExperimentTypes?.length
        ? this.filterExperimentTypes
        : this.experimentTypesFilter?.length
          ? this.experimentTypesFilter
          : ["planogram", "space_assortment"],
      page_size: 10000,
      group_by: this.selectedGroupByOption,
      group_by_level: this.selectedGroupByOption?.length + 1,
      search_text: this.searchText || null,
    };

    const pathValues = this.buildPublishedPathValues(this.parentSelectedRows);

    const groupedValues = new Map<string, any[]>();

    pathValues.forEach((entry) => {
      if (!entry.field) {
        return;
      }

      if (!groupedValues.has(entry.field)) {
        groupedValues.set(entry.field, []);
      }

      groupedValues
        .get(entry.field)!
        .push(...(Array.isArray(entry.value) ? entry.value : [entry.value]));
    });

    groupedValues.forEach((values, field) => {
      payload[field] = values;
    });

    this.commonApiCall(apiUrl, "postMethod", payload, (response: any) => {
      if (response?.experiments) {
        const experiments = response.experiments;
        this.syncPublishedSelectionState(experiments, "parent");
        this.getPlansForExperiment(
          this.getPublishedSelectedExperiments(),
          true,
        );
      } else {
        this.messageService.add({
          key: "w",
          severity: "warn",
          summary: "Something went wrong!",
          detail: "Failed to fetch published experiments.",
          sticky: true,
        });
      }
    });
  }

  private buildPublishedPathValues(
    selectedRows: any[],
  ): Array<{ dimension: string; field: string; value: any }> {
    const hierarchy = this.selectedGroupByOption || [];

    return (selectedRows.length ? selectedRows : [])
      .map((node: any, index: number) => {
        const dimension = node?.group_tag ?? hierarchy[index] ?? null;
        const rawValue =
          this.publishedGroupingService.isKeyAccountDimension(dimension) &&
          Array.isArray(node?.key_account_ids) &&
          node.key_account_ids.length > 0
            ? node.key_account_ids
            : node?.value ?? node?.label ?? node?.key ?? null;

        const value = Array.isArray(rawValue)
          ? rawValue
          : this.resolveGroupedFilterValue(dimension, rawValue);

        if (!dimension || !value) {
          return null;
        }

        return {
          dimension,
          field: this.getGroupedFilterField(dimension),
          value,
        };
      })
      .filter(Boolean) as Array<{
      dimension: string;
      field: string;
      value: any;
    }>;
  }

  getPublishExperimentListApiCall(
    groupByLevel: number = 1,
    parentNode?: any,
    groupPath: any[] = [],
    page: number = this.currentPage,
    pageSize: number = this.pageSize,
  ) {
    const payload = this.buildPublishedPayload(
      groupByLevel,
      parentNode,
      groupPath,
      page,
      pageSize,
    );

    const apiUrl = this.stateService.apiList.experiments.publishedExperiments;

    this.commonApiCall(apiUrl, "postMethod", payload, (response: any) => {
      const responseGroups = Array.isArray(response?.groups) ? response : [];
      const experiments = Array.isArray(response?.experiments)
        ? response.experiments
        : [];

      if (responseGroups.groups?.length) {
        if (groupByLevel === 1) {
          this.groupedPublishedData = this.normalizeGroupedData(
            responseGroups,
            groupByLevel,
          );
          this.expandedGroupedNodeIds = new Set(
            this.groupedPublishedData.groups.map((node: any) => node.id),
          );
        } else if (parentNode) {
          this.groupedPublishedData.group_by_level =
            responseGroups.group_by_level;
          this.groupedPublishedData.group_by = responseGroups.group_by;
          this.mergeGroupedPublishedNode(
            parentNode,
            responseGroups,
            groupByLevel,
          );
          parentNode.childrenLoaded = true;
        }

        this.resetToRoot();
        setTimeout(() => this.applyPreSelectedExperimentsAndPlans(), 0);
        return;
      }

      if (parentNode && experiments.length) {
        const mappedExperiments = this.transformExperimentItems(experiments);

        this.syncGroupedNodePayload(parentNode, {
          experiments: mappedExperiments,
          page: response.page,
          page_size: response.page_size,
          total: response.total,
          total_pages: response.total_pages,
        });
        parentNode.childrenLoaded = true;
        parentNode.expanded = true;
        this.groupedPublishedData = { ...this.groupedPublishedData };

        setTimeout(() => this.applyPreSelectedExperimentsAndPlans(), 0);
        return;
      }

      this.groupedPublishedData = [];
      this.expandedGroupedNodeIds.clear();

      this.resetToRoot();

      this.experimentList[this.currentTab] =
        this.transformExperimentItems(experiments);
      this.pageSize = response.page_size;
      this.currentPage = response.page;
      this.totalRecords = response.total;
      this.totalPages = response.total_pages;

      setTimeout(() => this.applyPreSelectedExperimentsAndPlans(), 0);
    });
  }

  private loadPublishedTab(): void {
    if (!this.selectedGroupByOption?.length) {
      this.callForGroupByChange();
      return;
    }

    this.getPublishExperimentListApiCall();
  }

  private transformExperimentItems(experiments: any[]): any[] {
    return this.publishedGroupingService.transformExperimentItems(experiments);
  }

  private buildPublishedPayload(
    groupByLevel: number = 1,
    parentNode?: any,
    groupPath: any[] = [],
    page: number = this.currentPage,
    pageSize: number = this.pageSize,
  ): any {
    const pathValues = this.buildPublishedPathValues(
      groupPath.length ? groupPath : parentNode ? [parentNode] : [],
    );

    const payload: any = {
      page,
      page_size: pageSize,
      key_account_ids: this.keyAccountFilterId
        ? [this.keyAccountFilterId]
        : this.filterKeyAccountIds,
      countries: this.filterCountries,
      regions: this.filterRegions,
      search_text: this.searchText,
      group_by: this.selectedGroupByOption || null,
      group_by_level: groupByLevel,
      experiment_types: this.filterExperimentTypes?.length
        ? this.filterExperimentTypes
        : this.experimentTypesFilter?.length
          ? this.experimentTypesFilter
          : ["planogram", "space_assortment"],
    };

    pathValues.forEach((entry) => {
      if (entry.field) {
        payload[entry.field] = Array.isArray(entry.value)
          ? entry.value
          : [entry.value];
      }
    });

    if (this.sorting.length) {
      payload["sort"] = this.sorting;
    }

    if (this.dateRange?.length) {
      payload["created_at"] = this.dateRange;
    }

    return payload;
  }

  private getGroupedFilterField(dimension: string | null): string | null {
    return this.publishedGroupingService.getGroupedFilterField(dimension);
  }

  private resolveGroupedFilterValue(dimension: string | null, value: any): any {
    return this.publishedGroupingService.resolveGroupedFilterValue(
      dimension,
      value,
      this.keyAccountList,
    );
  }

  private getNodeGroupKey(node: any): string {
    return this.publishedGroupingService.getNodeGroupKey(node);
  }

  private syncGroupedNodePayload(
    targetNode: any,
    payload: {
      experiments?: any[];
      page?: number;
      page_size?: number;
      total?: number;
      total_pages?: number;
    },
  ): void {
    this.publishedGroupingService.syncGroupedNodePayload(targetNode, payload);
  }

  private findGroupedPublishedNode(
    nodes: any[],
    targetKey: string,
  ): any | null {
    return this.publishedGroupingService.findGroupedPublishedNode(
      nodes,
      targetKey,
    );
  }

  private findGroupedPublishedPath(
    nodes: any[],
    targetKey: string,
    currentPath: any[] = [],
  ): any[] | null {
    return this.publishedGroupingService.findGroupedPublishedPath(
      nodes,
      targetKey,
      currentPath,
    );
  }

  private normalizeGroupedData(data: any, groupByLevel: number = 1) {
    return this.publishedGroupingService.normalizeGroupedData(
      data,
      groupByLevel,
    );
  }

  private mergeGroupedPublishedNode(
    targetNode: any,
    incomingGroups: any,
    groupByLevel: number,
  ): void {
    this.publishedGroupingService.mergeGroupedPublishedNode(
      targetNode,
      incomingGroups,
      groupByLevel,
    );
  }

  onGroupedRowExpand(event: { node: any; level: number }): void {
    if (!event?.node) {
      return;
    }

    const currentNode = event.node;

    if (currentNode?.childrenLoaded) {
      currentNode.expanded = true;
      return;
    }

    const groupPath = this.findGroupedPublishedPath(
      this.groupedPublishedData.groups,
      this.getNodeGroupKey(event.node),
    );

    const nextPage = 1;
    const nextPageSize = currentNode?.page_size ?? this.pageSize;

    currentNode.page = nextPage;
    currentNode.page_size = nextPageSize;

    this.getPublishExperimentListApiCall(
      event.level,
      currentNode,
      groupPath || [currentNode],
      nextPage,
      nextPageSize,
    );
  }

  groupPaginationAction(event: any) {
    const node = event?.data?.node;
    const page = event?.data?.page ?? node?.page ?? 1;
    const pageSize = event?.data?.page_size ?? node?.page_size ?? this.pageSize;

    if (!node) {
      this.getPublishExperimentListApiCall(1, undefined, [], page, pageSize);
      return;
    }

    const liveNode =
      this.findGroupedPublishedNode(
        this.groupedPublishedData.groups,
        this.getNodeGroupKey(node),
      ) || node;

    const groupPath = this.findGroupedPublishedPath(
      this.groupedPublishedData.groups,
      this.getNodeGroupKey(liveNode),
    );
    const groupByLevel =
      liveNode?.group_by_level ??
      node?.group_by_level ??
      event?.data?.level ??
      1;

    this.getPublishExperimentListApiCall(
      Math.max(1, groupByLevel + 1),
      liveNode,
      groupPath || [liveNode],
      page,
      pageSize,
    );
  }

  handleGroupByChange(event: any) {
    this.groupedPublishedData = {};
    this.currentPage = 1;
    this.pageSize = 20;
    this.selectedGroupByOption = event;
    this.callForGroupByChange("putMethod");
    this.getPublishExperimentListApiCall();
  }

  callForGroupByChange(apiMethod: any = "getMethod") {
    const payload = {
      group_by: this.selectedGroupByOption,
    };
    const apiUrl = this.stateService.apiList.experiments.getGroupedExperiments;
    this.commonApiCall(
      apiUrl,
      apiMethod,
      payload,
      (response: any, params: any) => {
        if (apiMethod === "getMethod") {
          if (response && response.group_by) {
            this.selectedGroupByOption = response.group_by;
          }
          this.getPublishExperimentListApiCall();
        }
      },
    );
  }

  private syncPublishedSelectionState(
    selectedItems: any[],
    source: "direct" | "parent" = "direct",
  ) {
    const selectedExperiments = (selectedItems || []).filter(
      (item: any) =>
        item &&
        item.id !== undefined &&
        item.id !== null &&
        ["planogram", "space_assortment"].includes(item?.experiment_type),
    );

    const nextPublishedIds: Set<string> = new Set<string>(
      selectedExperiments.map((item: any) => String(item.id)),
    );
    const sourceSelectionIds =
      source === "parent"
        ? this.parentPublishedSelectionIds
        : this.directPublishedSelectionIds;
    const otherSelectionIds =
      source === "parent"
        ? this.directPublishedSelectionIds
        : this.parentPublishedSelectionIds;

    this.selectedExperiments
      .filter(
        (item: any) =>
          sourceSelectionIds.has(String(item.id)) &&
          !nextPublishedIds.has(String(item.id)) &&
          !otherSelectionIds.has(String(item.id)),
      )
      .forEach((expData: any) => {
        this.setExperimentSelectionState(expData, false, true);
      });

    selectedExperiments.forEach((expData: any) => {
      this.setExperimentSelectionState(expData, true, true);
    });

    if (source === "parent") {
      this.parentPublishedSelectionIds = nextPublishedIds;
    } else {
      this.directPublishedSelectionIds = nextPublishedIds;
    }

    this.refreshPublishedSelectionIds();
  }

  private syncSinglePublishedSelectionState(
    item: any,
    checked: boolean,
    source: "direct" | "parent" = "direct",
  ): void {
    if (!item?.id) {
      return;
    }

    const itemId = String(item.id);
    const sourceSelectionIds =
      source === "parent"
        ? this.parentPublishedSelectionIds
        : this.directPublishedSelectionIds;
    const otherSelectionIds =
      source === "parent"
        ? this.directPublishedSelectionIds
        : this.parentPublishedSelectionIds;

    if (checked) {
      sourceSelectionIds.add(itemId);
    } else {
      sourceSelectionIds.delete(itemId);

      if (!otherSelectionIds.has(itemId)) {
        this.setExperimentSelectionState(item, false, true);
      }
    }

    this.refreshPublishedSelectionIds();
  }

  private refreshPublishedSelectionIds(): void {
    this.publishedSelectionIds = new Set<string>([
      ...this.directPublishedSelectionIds,
      ...this.parentPublishedSelectionIds,
    ]);
  }

  private getPublishedSelectedExperiments(): any[] {
    return this.selectedExperiments.filter(
      (item: any) =>
        item &&
        this.publishedSelectionIds.has(String(item.id)) &&
        ["planogram", "space_assortment"].includes(item?.experiment_type),
    );
  }

  private syncParentSelectionState(parentSelectedItems: any[]) {
    this.parentSelectedRows = (parentSelectedItems || []).filter(
      (item: any) =>
        item && item.group_tag !== undefined && item.group_tag !== null,
    );
  }

  // Drop folder-derived experiments the user excluded inside a selected folder.
  private resolveFolderExclusions(
    experimentIds: string[],
    callback: (ids: string[]) => void,
  ): void {
    const candidates = experimentIds.filter(
      (id) => this.experimentSelectionMap[id] !== false,
    );
    if (!this.excludedFolderIds.size) {
      callback(candidates);
      return;
    }

    // Excluded folders, plus selected folders re-checked inside an excluded one.
    const folderIds = [
      ...Array.from(this.excludedFolderIds),
      ...Array.from(this.selectedFolderIds).filter((id) =>
        (this.selectionPathMap.get(id) || []).some((a) =>
          this.excludedFolderIds.has(a),
        ),
      ),
    ];
    const depth = (id: string) => (this.selectionPathMap.get(id) || []).length;
    const loaderKey = "folder-exclusions";

    this.stateService.addCalledApi(loaderKey);
    forkJoin(
      folderIds.map((folderId) =>
        this.httpService
          .postMethod(
            this.stateService.apiList.planogram.getFolderExperiments(folderId),
            {
              experiment_types: this.experimentTypesFilter,
              include_nested: true,
              fields: ["id"],
            },
          )
          .pipe(
            map(
              (res: any) =>
                new Set<string>(
                  (res?.experiments || []).map((exp: any) => exp.id),
                ),
            ),
          ),
      ),
    ).subscribe({
      next: (nestedSets: Set<string>[]) => {
        this.stateService.removeCalledApi(loaderKey);
        callback(
          candidates.filter((expId) => {
            // Folder sets are nested, so the deepest containing folder is the nearest ancestor.
            let nearest = "";
            for (let i = 0; i < folderIds.length; i++) {
              if (
                nestedSets[i].has(expId) &&
                (!nearest || depth(folderIds[i]) > depth(nearest))
              ) {
                nearest = folderIds[i];
              }
            }
            return !nearest || !this.excludedFolderIds.has(nearest);
          }),
        );
      },
      error: () => {
        this.stateService.removeCalledApi(loaderKey);
        this.messageService.add({
          key: "w",
          severity: "warn",
          summary: "Something went wrong!",
          detail: "Failed to resolve excluded folder contents.",
          sticky: true,
        });
      },
    });
  }
}
